
import Vue, { PropType } from 'vue';

import CardCollapse from '@/components/common/CardCollapse.vue';
import AssignRuleModal from '@/components/modal/AssignRuleModal.vue';
import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';
import RuleTable from '@/components/common/RuleTable.vue';
import {
	AppliedRule,
	AssignRuleForm,
	Rule,
	RuleTemplate,
	RulespecMovementSettings,
	RulespecMovement,
	RulespecDefault,
	RulespecLightingSettings,
} from '@/interfaces/rules';
import { Device, RoomZoneDeviceTypes } from '@/interfaces/locations';
import { Controller } from '@/interfaces/controllers';

type rulePillVariant = 'warning' | 'success' | 'danger';

export default Vue.extend({
	components: {
		CardCollapse,
		AssignRuleModal,
		ConfirmActionModal,
		RuleTable,
	},
	props: {
		appliedRule: { type: Object as PropType<Rule> },
		ruleApplications: { type: Array as PropType<AppliedRule[]> },
		rules: { type: Array as PropType<RuleTemplate[]> },
		movementRoomSensors: { type: Object as PropType<RoomZoneDeviceTypes> },
		controllers: { type: Array as PropType<Controller[]> },
		userPermission: { type: Boolean, default: true },
	},
	computed: {
		activeRule(): RulespecMovement | undefined {
			const rule: RuleTemplate | undefined = this.rules?.find(
				(r) => r.id == this.appliedRule?.fk_rule_template
			);
			if (rule) {
				return rule?.rulespec[0].rules[0] as RulespecMovement;
			} else {
				return this.appliedRule?.rulespec[0].rules.filter(
					(r): r is RulespecMovement => r.type === 'movement'
				)?.[0];
			}
		},

		standByRule(): RulespecMovement | undefined {
			const rule: RuleTemplate | undefined = this.rules?.find(
				(r) => r.id == this.appliedRule?.fk_rule_template
			);
			if (rule) {
				return rule?.rulespec[0].rules[1] as RulespecMovement;
			} else {
				return this.appliedRule?.rulespec[0].rules.filter(
					(r): r is RulespecMovement => r.type === 'movement'
				)?.[1];
			}
		},
		inactiveRule(): RulespecDefault | undefined {
			return this.appliedRule?.rulespec[0].rules.find(
				(r): r is RulespecDefault => r.type === 'default' || r.type === 'dim'
			);
		},
		lightingSettingsRule(): RulespecLightingSettings {
			return (
				this.appliedRule?.rulespec[0].rules.find(
					(r): r is RulespecLightingSettings => r.type === 'lighting_settings'
				) ?? { type: 'lighting_settings', ramp_up_time: 10, ramp_down_time: 10 }
			);
		},
		ruleStatus(): { text: string; variant: rulePillVariant } {
			if (typeof this.ruleApplications === 'undefined') {
				return {
					text: this.$t('rule-detail.rule-application.no-applied-error-text').toString(),
					variant: 'warning',
				};
			}
			const states = new Map<string, rulePillVariant>([
				['new', 'warning'],
				['running', 'success'],
				['ended', 'danger'],
				['timeout', 'danger'],
				['limbo', 'danger'],
				['rejected', 'danger'],
				['refused', 'danger'],
				['disabled', 'danger'],
				['cancelled', 'danger'],
				['starting', 'warning'],
				['undeliverable', 'danger'],
				['error', 'danger'],
			]);
			const order = ['danger', 'warning', 'success'];

			const variants = this.ruleApplications
				.map((ar) => states.get(ar.status))
				.filter((s): s is rulePillVariant => typeof s !== 'undefined');
			const variant = order[Math.min(...variants.map((v) => order.indexOf(v)))];

			switch (variant) {
				case 'danger':
					return { text: this.$t('rule-detail.rule-application.error-text').toString(), variant };
				case 'warning':
					return { text: this.$t('rule-detail.rule-application.warning-text').toString(), variant };
				case 'success':
					return { text: this.$t('rule-detail.rule-application.ok-text').toString(), variant };
				default:
					return {
						text: this.$t('rule-detail.rule-application.error-text').toString(),
						variant: 'danger',
					};
			}
		},
	},
	methods: {
		sensors(type: string): string[] {
			return (
				this.appliedRule?.rulespec[0].rules
					.find((r): r is RulespecMovementSettings => r.type === type)
					?.sensors.filter((s) => s !== '0000000000000000') ?? []
			);
		},

		sensorNames(type: string): string[] {
			const names: string[] = [];
			let sensors = 0;
			for (const guid of this.sensors(type)) {
				const controller: Controller | undefined = this.controllers.find((c) => c.guid === guid);
				if (controller !== undefined) {
					let sensor: Device | undefined;
					for (const movementSensorsKey of Object.keys(this.movementRoomSensors.devices)) {
						sensor = this.movementRoomSensors.devices[movementSensorsKey].find(
							(s) => s.fk_controller === controller.id
						);

						if (['relative_light_settings', 'follower_settings'].includes(type)) {
							if (
								sensor &&
								sensor.is_lightlevel_sensor &&
								sensor.is_movement_sensor &&
								sensor.is_luminaire
							) {
								sensors += 1;
							}
						}

						if (sensor) {
							break;
						}
					}
					names.push(sensor?.name ?? this.$t('Unknown').toString());
				} else {
					names.push(this.$t('Unknown').toString());
				}
			}
			if (
				['relative_light_settings', 'follower_settings'].includes(type) &&
				names.length == sensors &&
				!this.sensors('movement_settings')
			) {
				return [];
			}
			return names;
		},

		showModal(): void {
			this.$bvModal.show('modal-assign-rule');
		},

		assignRule(assignRuleForm: AssignRuleForm): void {
			this.$emit('assignRule', assignRuleForm);
		},

		onRuleRemove(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.ruleConfirmModal as any).show();
		},

		onRuleRemoveConfirm(): void {
			this.$emit('removeRule');
			this.closeRemoveModal();
		},

		closeRemoveModal(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.ruleConfirmModal as any).hide();
		},
	},
});
